import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Space, Button, Layout, Menu, Badge, notification, Flex, Tooltip } from 'antd';
import {
    UserOutlined,
    MessageFilled,
    BellOutlined,
    FolderOpenFilled,
    HistoryOutlined,
    CalendarFilled,
    InfoCircleFilled,
    InfoCircleOutlined,
} from '@ant-design/icons';

import Garland from '@controls/easter-eggs/garland/garland';
import ExchangeRateInfo from '@components/exchange-rate-info/exchange-rate-info';
import PushPersonalModal from '@components/notifications/push-personal-modal';

import { isNewYearAroundUs } from '@extensions/utils';

import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setCurrentMenuItem, cleanupStates, setRestartServiceRequired } from '@store/actions';
import { serverFetch } from '@flc/server';

import { INotification } from '@entities/notification';
import { IUserSession } from '@entities/user-session';

import { UserType } from '@enums/user-type';

import {
    LogisticsIcon,
    ImportTruckIcon,
    DeliveryTruckIcon,
    BoxesIcon,
    BillIcon,
    MainLogoIcon,
    AccountingIcon,
    MiniLogoIcon,
} from '@icons/index';

import '@core/index.css';

interface IMainLayout {
    appVersion: string | undefined;
}

const MainLayout = (props: IMainLayout) => {
    const { appVersion } = props;
    const [api, contextHolder] = notification.useNotification();

    const navigate = useNavigate();

    const { Header, Content, Sider } = Layout;

    const location = useLocation();

    const d = useAppDispatch();
    const currentMenuItem = useAppSelector<string>((s) => s.currentMenuItem);
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [newNotifications, setNewNotifications] = useState<Array<INotification>>([]);
    const [newDocumentsQty, setNewDocumentsQty] = useState<number | undefined>();
    const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);

    const [siderCollapsed, setSiderCollapsed] = useState<boolean>(false);

    function getItem(label: any, key: string, urlTo?: string, icon?: any, children?: Array<any>) {
        let itemlabel = urlTo ? <Link to={urlTo}>{label}</Link> : label;

        return {
            key,
            icon,
            children,
            label: itemlabel,
        };
    }

    useEffect(() => {
        const items = location.pathname.replace('/flc', '').split('/');

        let key = items[1];
        d(setCurrentMenuItem(key));

        getNewNotifications();

        if (userSession.userSettings?.showDocuments) {
            getNewDocuments();
        }

        const interval = setInterval(() => {
            getNewNotifications();

            if (userSession.userSettings?.showDocuments) {
                getNewDocuments();
            }
        }, 5000); //per 5 sec

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setNotificationsOpen(newNotifications.length > 0);
    }, [newNotifications]);

    const onLogout = () => {
        serverFetch(`auth/logout`, { method: 'POST' })
            .then(() => {
                d(cleanupStates());
            })
            .catch(() => {
                d(cleanupStates());
            });
    };

    const getNewNotifications = async () => {
        d(setRestartServiceRequired(false));

        await serverFetch('notifications/unread', { method: 'GET' })
            .then((data: Array<INotification>) => {
                setNewNotifications(data);
            })
            .catch((ex) => {
                setNewNotifications([]);
            });
    };

    const getNewDocuments = async () => {
        d(setRestartServiceRequired(false));

        await serverFetch('documents/new', { method: 'GET' })
            .then((data) => {
                setNewDocumentsQty(data);
            })
            .catch((ex) => {
                setNewDocumentsQty(undefined);
            });
    };

    const onMenuClick = (e: any) => {
        d(setCurrentMenuItem(e.key));
    };

    const items: Array<any> = [];

    if (userSession.userSettings?.showOrders) {
        if (userSession.type === UserType.MainClient || userSession.type === UserType.SubClient) {
            items.push(getItem('Заказы', 'orders', '/flc/orders', <LogisticsIcon />));
        } else if (userSession.type === UserType.Supplier || userSession.type === UserType.Employee) {
            items.push(getItem('Импорт', 'import', '/flc/import', <ImportTruckIcon />));
            items.push(getItem('Доставка', 'delivery', '/flc/delivery', <DeliveryTruckIcon />));
        }

        items.push(getItem('Планирование погрузок', 'loadingplans', '/flc/loadingplans', <CalendarFilled />));
    }

    if (userSession.userSettings?.showInventory) items.push(getItem('Остатки', 'remainings', '/flc/remainings', <BoxesIcon />));

    if (userSession.userSettings?.showBills) items.push(getItem('Счета', 'billheaders', '/flc/billheaders', <BillIcon />));

    if (userSession.userSettings?.showBalances)
        items.push(getItem('Балансы', 'balances', '/flc/balances', <AccountingIcon style={{ fontSize: 22 }} />));

    if (userSession.userSettings?.showTransactions)
        items.push(getItem('История операций', 'transactions', '/flc/transactions', <HistoryOutlined />));

    if (userSession.userSettings?.showDocuments) {
        items.push(
            getItem(
                <>
                    Документы
                    {newDocumentsQty ? <Badge count={newDocumentsQty} color='blue' overflowCount={10} offset={[10, -1]}></Badge> : ''}
                </>,
                'files',
                '/flc/files',
                <FolderOpenFilled />,
            ),
        );
    }

    items.push(
        getItem(
            <>
                Уведомления
                {newNotifications && <Badge count={newNotifications.length} overflowCount={10} offset={[10, -1]}></Badge>}
            </>,
            'notifications',
            '/flc/notifications',
            <MessageFilled />,
        ),
    );

    items.push(getItem('Профиль', 'useraccount', '/flc/useraccount', <UserOutlined />));

    items.push(getItem('Информация', 'info', '/flc/info', <InfoCircleFilled />));

    const getLogo = () => {
        return siderCollapsed ? (
            <div className='logo'>
                <MiniLogoIcon style={{ fontSize: 30, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        ) : (
            <div className='logo'>
                <div className='sub-logo'>
                    <div style={{ marginTop: 5 }}>Срезка</div>
                </div>
                <MainLogoIcon style={{ fontSize: 38, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        );
    };

    return (
        <>
            <Layout style={{ height: '100vh', minHeight: '100vh' }}>
                <Sider
                    width={250}
                    collapsible={true}
                    collapsed={siderCollapsed}
                    onCollapse={(e) => setSiderCollapsed(e)}
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        zIndex: 100,
                    }}
                >
                    {getLogo()}
                    <Flex style={{ height: 'calc(100vh - 120px)' }} vertical justify='space-between'>
                        <Menu selectedKeys={[currentMenuItem]} mode='inline' items={items} onClick={onMenuClick} />
                        <div className='app-version'>Версия {appVersion}</div>
                    </Flex>
                </Sider>
                <Layout>
                    <Header className='main-header'>
                        <Flex align='center' justify='flex-end' gap='small'>
                            {isNewYearAroundUs() && <Garland />}
                            <Flex align='center' justify='flex-end' gap='small' style={{ background: '#ffffff', position: 'relative' }}>
                                <Button
                                    shape='circle'
                                    size='large'
                                    variant='link'
                                    color='blue'
                                    icon={
                                        <Tooltip title='Информация' placement='bottom' color='blue'>
                                            <InfoCircleOutlined style={{ fontSize: 22, marginBottom: -3 }} />
                                        </Tooltip>
                                    }
                                    onClick={() => {
                                        navigate(`/flc/info`);
                                    }}
                                />
                                <Button
                                    shape='circle'
                                    size='large'
                                    variant='link'
                                    color='blue'
                                    icon={
                                        <Tooltip title='Уведомления' placement='bottom' color='blue'>
                                            <BellOutlined style={{ fontSize: 22, marginBottom: -3 }} />
                                            <Badge
                                                count={newNotifications.length}
                                                overflowCount={10}
                                                size='small'
                                                style={{
                                                    marginLeft: -10,
                                                    marginBottom: 25,
                                                }}
                                            />
                                        </Tooltip>
                                    }
                                    onClick={() => {
                                        d(setCurrentMenuItem('notifications'));
                                        navigate(`/flc/notifications`);
                                    }}
                                />
                                <ExchangeRateInfo
                                    setInternalUsdExchangeRate={false}
                                    api={api}
                                    useCBExchangeRate={userSession.userSettings?.useCBExchangeRate}
                                    useInternalExchangeRate={userSession.userSettings?.useInternalExchangeRate}
                                    serverFetch={serverFetch}
                                />

                                <Space className='user-account-info'>
                                    <div className='user'>{userSession.fullName}</div>
                                </Space>
                                <Button variant='link' color='blue' className='logout' onClick={() => onLogout()}>
                                    Выход
                                </Button>
                            </Flex>
                        </Flex>
                    </Header>
                    <Content className='main-content'>
                        <Outlet />
                    </Content>
                </Layout>
            </Layout>

            {notificationsOpen && (
                <PushPersonalModal
                    items={newNotifications}
                    onClose={() => setNotificationsOpen(false)}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {contextHolder}
        </>
    );
};

export default MainLayout;
